import type { PaginationState, SortingState } from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo, useState } from 'react'
import { FAIcon } from './fa-icon'
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui'

const DEFAULT_PAGE_SIZE = 25

interface PaginationOptions {
  pageSize?: number
  itemsText?: (n: number) => string
}

interface DataTableProps<TData extends { id: string }, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]

  noDataText?: string
  pagination?: boolean | PaginationOptions
  initialSort?: SortingState
}

/**
 * IMPORTANT! Because Tanstack Table currently does not support React Compiler,
 * columns and data must be stable between renders. Use useMemo where appropriate.
 */
export function DataTable<TData extends { id: string }, TValue>({
  columns,
  data,

  noDataText,
  pagination: paginationOpts,
  initialSort = [],
}: DataTableProps<TData, TValue>) {
  const translations = useTranslate({
    noData: 'components.data-table.no-data',
    pageNo: ['components.data-table.page-no', { page: 0, total: 0 }],
    items: ['components.data-table.items', { n: 0 }],
  })

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: !paginationOpts
      ? data.length
      : paginationOpts === true
      ? DEFAULT_PAGE_SIZE
      : paginationOpts.pageSize ?? DEFAULT_PAGE_SIZE,
  })

  const normalizedColumns = useMemo<ColumnDef<TData, TValue>[]>(
    () =>
      columns.map(col => ({
        ...col,
        enableSorting: col.enableSorting ?? false,
      })),
    [columns]
  )

  // Tanstack Table currently does not support React Compiler
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    columns: normalizedColumns,
    data,
    getRowId: row => row.id,
    getCoreRowModel: getCoreRowModel(),
    state: {
      pagination,
    },
    initialState: {
      sorting: initialSort,
    },
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
    onPaginationChange: setPagination,
  })

  const paginationOptions = useMemo<PaginationOptions | null>(() => {
    if (!paginationOpts) return null

    return typeof paginationOpts === 'boolean' ? {} : paginationOpts
  }, [paginationOpts])

  return (
    <div>
      <div className="overflow-hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(group => (
              <TableRow key={group.id}>
                {group.headers.map(header => {
                  const center =
                    !!header.column.columnDef.meta?.center ||
                    !!header.column.columnDef.meta?.centerHeader

                  const sortable = header.column.getCanSort()
                  const sortDir = header.column.getIsSorted()

                  return (
                    <TableHead
                      key={header.id}
                      center={center}
                      sortable={sortable}
                      sortDir={sortDir}
                      onClick={
                        !sortable
                          ? undefined
                          : header.column.getToggleSortingHandler()
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map(row => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map(cell => {
                    const center =
                      !!cell.column.columnDef.meta?.center ||
                      !!cell.column.columnDef.meta?.centerCell

                    const narrow = !!cell.column.columnDef.meta?.narrow

                    return (
                      <TableCell key={cell.id} center={center} narrow={narrow}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  className="h-24 text-center"
                  colSpan={columns.length}
                >
                  {noDataText ?? translations.noData}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {!!paginationOptions && (
        <div className="flex items-center justify-end gap-4 py-4">
          <div>
            {paginationOptions.itemsText?.(data.length) ??
              translations.items({ n: data.length })}
          </div>

          <span>|</span>

          <div>
            {translations.pageNo({
              page: pagination.pageIndex + 1,
              total: Math.max(1, table.getPageCount()),
            })}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon-sm"
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.previousPage()}
            >
              <FAIcon icon="chevron-left" />
            </Button>

            <Button
              variant="outline"
              size="icon-sm"
              disabled={!table.getCanNextPage()}
              onClick={() => table.nextPage()}
            >
              <FAIcon icon="chevron-right" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
